import {
  loadNewsletterScript,
  loadNewsletterTrackingScript,
} from '@components/components/Newsletter/NewsletterScripts';
import {
  useLocalStorage,
  useSessionStorage,
  useTracking,
} from '@components/hooks';
import styles from '@components/components/Newsletter/NewsletterToastNotification/NewsletterToastNotification.module.scss';
import { usePageData } from '@components/context';
import FormDecider from '@components/components/Newsletter/FormDecider';
import React, { useEffect, useMemo } from 'react';
import {
  Content,
  FontAwesomeProIcon,
  Heading,
  List,
  ListItem,
  Modal,
} from '@wienenergiegithub/ui-next';
import { useCookieConsent } from '@wienenergiegithub/modules-next/components';
import { useDialogStore } from '@ariakit/react';

const NewsletterToastNotification = () => {
  const {
    newsletterToastNotification: {
      blacklist,
      businessContent,
      businessHeading,
      businessListItems,
      businessTextContent,
      content,
      heading,
      isActive,
      listItems,
      textContent,
      timeout,
      whitelist,
    },
    rootLevel: { slug: area },
  } = usePageData();

  const [storage, setStorage] = useLocalStorage('newsletter-notification');
  const [isRegistered] = useSessionStorage('newsletter', false);
  const cookieConsent = useCookieConsent();
  const dialog = useDialogStore();
  const hasCookieConsent = useMemo(
    () => cookieConsent.services.length > 0,
    [cookieConsent.services.length],
  );
  const track = useTracking();
  const newsletterDecider =
    whitelist?.length > 0
      ? whitelist?.includes(window.location.pathname)
      : !blacklist?.includes(window.location.pathname);

  useEffect(() => {
    let timeoutId;

    if (
      isActive &&
      !isRegistered &&
      !storage &&
      hasCookieConsent &&
      newsletterDecider
    ) {
      timeoutId = setTimeout(() => {
        loadNewsletterScript();
        loadNewsletterTrackingScript();
        dialog.show();
        track({
          event: 'newsletter_anmeldung',
          options: 'displayed_toast',
        });
      }, timeout * 1000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [hasCookieConsent]);

  const handleClose = e => {
    e.preventDefault();
    track({
      event: 'newsletter_anmeldung',
      options: 'closed_toast',
    });
    dialog.hide();
    // we need to remove the scripts from the DOM, otherwise they will be loaded again
    document.body.removeChild(document.getElementById('form-loader'));
    document.body.removeChild(document.getElementById('ws-useTracking'));
    window.MsCrmMkt = undefined;
    setStorage(true);
  };

  if (!isActive) {
    return null;
  }

  return (
    <Modal
      autoFocus={false}
      className={styles.newsletterModal}
      header="Newsletteranmeldung"
      onClickBackdrop={() => handleClose}
      onClose={handleClose}
      store={dialog}
      theme={area === 'business' ? 'business' : 'secondary'}
    >
      <div className={styles.container}>
        <div className={styles.contentWrapper}>
          {area === 'business' ? (
            <>
              <Heading as="h1" size="h3">
                {businessHeading}
              </Heading>
              <Content size="lg">{businessContent}</Content>
              {businessTextContent && (
                <Content as="div" size="md" withParser>
                  {businessTextContent}
                </Content>
              )}
              {businessListItems?.length > 0 && (
                <List
                  className={styles.listWrapper}
                  icon={
                    <FontAwesomeProIcon
                      icon="check"
                      role="presentation"
                      type="regular"
                    />
                  }
                  type="checkmark"
                >
                  {businessListItems?.map(({ id, text }) => (
                    <ListItem key={id}>{text}</ListItem>
                  ))}
                </List>
              )}
            </>
          ) : (
            <>
              <Heading as="h1" size="h3">
                {heading}
              </Heading>
              <Content size="lg">{content}</Content>
              {textContent && (
                <Content as="div" size="md" withParser>
                  {textContent}
                </Content>
              )}{' '}
              {listItems?.length > 0 && (
                <List
                  className={styles.listWrapper}
                  icon={
                    <FontAwesomeProIcon
                      icon="check"
                      role="presentation"
                      type="regular"
                    />
                  }
                  type="checkmark"
                >
                  {listItems?.map(({ id, text }) => (
                    <ListItem key={id}>{text}</ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </div>
        <div>
          <FormDecider area={area} form="subscribe" isOverlay />
        </div>
      </div>
    </Modal>
  );
};

export default NewsletterToastNotification;
